import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SubscriptionBanner.css';

const SubscriptionBanner = ({ user }) => {
  const { t } = useTranslation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (user && user.role === 'STUDENT') {
      // Check if student is from UFC (free access)
      const establishmentName = user.student?.establishment?.name;
      const isUFCStudent = establishmentName && (
        establishmentName.includes('U.F.C') || 
        establishmentName.includes('Université de la Formation Continue') ||
        establishmentName.includes('Formation Continue') ||
        establishmentName.includes('UFC')
      );
      
      console.log('🔍 SubscriptionBanner: User data:', user);
      console.log('🔍 SubscriptionBanner: Establishment name:', establishmentName);
      console.log('🔍 SubscriptionBanner: Is UFC student:', isUFCStudent);
      
      if (isUFCStudent) {
        // Show welcome message for UFC students
        setSubscriptionStatus({
          status: 'ufc_welcome',
          hasFullAccess: true,
          verifiedAt: null,
          expiresAt: null
        });
      } else {
        // Show subscription status for other students
        // Read from user.student object where subscription data is stored
        const student = user.student || {};
        setSubscriptionStatus({
          status: student.subscriptionStatus || 'unverified_or_unpaid',
          hasFullAccess: student.hasFullLibraryAccess || false,
          verifiedAt: student.subscriptionVerifiedAt,
          expiresAt: student.subscriptionExpiresAt
        });
      }
      setIsVisible(true);
    }
  }, [user]);

  if (!isVisible || !subscriptionStatus) {
    return null;
  }

  const getStatusMessage = () => {
    switch (subscriptionStatus.status) {
      case 'ufc_welcome':
        return {
          message: t('subscription.ufcWelcome'),
          type: 'success',
          icon: '🎓'
        };
      case 'verified_and_paid':
        return {
          message: t('subscription.verifiedAndPaid'),
          type: 'success',
          icon: '✅'
        };
      case 'verified_access':
        return {
          message: t('subscription.verifiedAccess'),
          type: 'success',
          icon: '✅'
        };
      case 'expired':
        return {
          message: t('subscription.expired'),
          type: 'error',
          icon: '❌'
        };
      case 'unverified_or_unpaid':
        // Determine specific reason for limited access
        const isAccountActivated = user?.isActivated || user?.student?.isActivated;
        const isEstablishmentActivated = user?.isEstablishmentActivated;
        
        // Priority: Account -> Establishment -> Subscription
        if (!isAccountActivated) {
          return {
            message: t('subscription.accountNotActivated'),
            type: 'warning',
            icon: '⚠️'
          };
        } else if (!isEstablishmentActivated) {
          return {
            message: t('subscription.establishmentNotActivated'),
            type: 'warning',
            icon: '⚠️'
          };
        } else {
          return {
            message: t('subscription.subscriptionNotPaid'),
            type: 'warning',
            icon: '⚠️'
          };
        }
      case 'account_not_activated':
        return {
          message: t('subscription.accountNotActivated'),
          type: 'warning',
          icon: '⚠️'
        };
      case 'establishment_not_activated':
        return {
          message: t('subscription.establishmentNotActivated'),
          type: 'warning',
          icon: '⚠️'
        };
      case 'subscription_not_paid':
        return {
          message: t('subscription.subscriptionNotPaid'),
          type: 'warning',
          icon: '⚠️'
        };
      default:
        return {
          message: t('subscription.unknown'),
          type: 'info',
          icon: 'ℹ️'
        };
    }
  };

  const statusInfo = getStatusMessage();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className={`subscription-banner ${statusInfo.type}`}>
      <div className="subscription-banner-content">
        <div className="subscription-banner-icon">
          {statusInfo.icon}
        </div>
        <div className="subscription-banner-text">
          <div className="subscription-banner-title">
            {statusInfo.message}
          </div>
          {subscriptionStatus.verifiedAt && (
            <div className="subscription-banner-details">
              {t('subscription.verifiedOn')}: {formatDate(subscriptionStatus.verifiedAt)}
            </div>
          )}
          {subscriptionStatus.expiresAt && subscriptionStatus.status === 'verified_and_paid' && (
            <div className="subscription-banner-details">
              {t('subscription.expiresOn')}: {formatDate(subscriptionStatus.expiresAt)}
            </div>
          )}
        </div>
        <button 
          className="subscription-banner-close"
          onClick={() => setIsVisible(false)}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
